import type { ChildNode } from "domhandler"
import DOMPurify from "isomorphic-dompurify"
import parse, { Element, type HTMLReactParserOptions } from "html-react-parser"
import Image from "next/image"
import React from "react"

export interface RichTextHeading {
  text: string
  level: number
  id: string
}

type ParseRichTextHtmlOptions = {
  collectHeadings?: boolean
  imageContainerClassName?: string
  portraitImageContainerClassName?: string
  slugCounts?: Map<string, number>
}

const headingTags = new Set(["h2"])

const normalizeSlug = (value: string) =>
  decodeURIComponent(value)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()

const extractTextFromNodes = (nodes: ChildNode[]): string =>
  nodes
    .map((node) => {
      if (node.type === "text" && "data" in node) {
        return node.data
      }

      if ("children" in node && Array.isArray(node.children)) {
        return extractTextFromNodes(node.children)
      }

      return ""
    })
    .join(" ")
    .replace(/\s+/g, " ")
    .trim()

const createHeadingId = (value: string, slugCounts: Map<string, number>) => {
  const baseSlug =
    normalizeSlug(value)
      .replace(/ß/g, "ss")
      .replace(/[^a-z0-9\s-]/g, "")
      .trim()
      .replace(/\s+/g, "-") || "section"

  const count = slugCounts.get(baseSlug) ?? 0
  const nextCount = count + 1
  slugCounts.set(baseSlug, nextCount)

  return nextCount === 1 ? baseSlug : `${baseSlug}-${nextCount}`
}

export const parseRichTextHtml = (
  inputHtml: string,
  options: ParseRichTextHtmlOptions = {}
): { content: React.ReactNode; headers: RichTextHeading[] } => {
  const sanitizedHtml = DOMPurify.sanitize(inputHtml)
  const headers: RichTextHeading[] = []
  const slugCounts = options.slugCounts ?? new Map<string, number>()

  const parserOptions: HTMLReactParserOptions = {
    replace(node, index) {
      if (!(node instanceof Element)) {
        return
      }

      if (options.collectHeadings && headingTags.has(node.name)) {
        const text = extractTextFromNodes(node.children)
        if (text) {
          const id = createHeadingId(text, slugCounts)
          headers.push({
            text,
            level: Number(node.name.replace("h", "")),
            id,
          })
          node.attribs = { ...node.attribs, id }
        }
        // return undefined — html-react-parser renders the (now mutated) element normally
        return
      }

      if (node.name === "img") {
        const imgWidth = Number(node.attribs.width)
        const imgHeight = Number(node.attribs.height)
        const isPortrait = imgHeight > imgWidth
        const wrapperClassName = isPortrait
          ? options.portraitImageContainerClassName
          : options.imageContainerClassName
        const image = (
          <Image
            key={index}
            id={node.attribs.id}
            alt={node.attribs.alt}
            height={imgHeight}
            width={imgWidth}
            className={node.attribs.class}
            src={`${process.env.NEXT_PUBLIC_CONTENT_URL}${node.attribs.src}`}
          />
        )

        return wrapperClassName ? <div className={wrapperClassName}>{image}</div> : image
      }
    },
  }

  return {
    headers,
    content: parse(sanitizedHtml, parserOptions),
  }
}
